import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { TASKS } from "@/constants/testIds";
import {
    Plus, ClipboardList, X, MessageSquare, ArrowRight,
    CheckCircle2, Clock, PauseCircle, PlayCircle, Send,
    ExternalLink, Loader2,
} from "lucide-react";

const STATUSES = [
    { key: "open", label: "Open", icon: ClipboardList },
    { key: "in_progress", label: "In Progress", icon: PlayCircle },
    { key: "blocked", label: "Blocked", icon: PauseCircle },
    { key: "done", label: "Done", icon: CheckCircle2 },
];

function fmtWhen(iso) {
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return "just now";
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch {
        return iso;
    }
}

export default function TasksPage() {
    const { user, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [assignables, setAssignables] = useState([]);
    const [forgeConfig, setForgeConfig] = useState(null);
    const [statusDraft, setStatusDraft] = useState("");
    const [statusNote, setStatusNote] = useState("");
    const [comment, setComment] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get("/tasks");
            setTasks(res.data);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        api.get("/config").then((r) => setForgeConfig(r.data)).catch(() => {});
        if (isAdmin) {
            api.get("/users/assignable").then((r) => setAssignables(r.data)).catch(() => {});
        }
    }, [isAdmin]);

    const selected = useMemo(
        () => tasks.find((t) => t.id === selectedId) || null,
        [tasks, selectedId],
    );

    useEffect(() => {
        if (selected) setStatusDraft(selected.status);
    }, [selected]);

    const groups = useMemo(() => {
        const g = { open: [], in_progress: [], blocked: [], done: [] };
        tasks.forEach((t) => {
            const k = STATUSES.find((s) => s.key === t.status) ? t.status : "open";
            g[k].push(t);
        });
        return g;
    }, [tasks]);

    const openDetails = async (id) => {
        try {
            const res = await api.get(`/tasks/${id}`);
            setTasks((prev) => prev.map((t) => (t.id === id ? res.data : t)));
            setSelectedId(id);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        }
    };

    const saveStatus = async () => {
        if (!selected) return;
        setBusy(true);
        try {
            const res = await api.put(`/tasks/${selected.id}/status`, {
                status: statusDraft,
                note: statusNote,
            });
            setTasks((prev) => prev.map((t) => (t.id === selected.id ? res.data : t)));
            setStatusNote("");
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const postComment = async () => {
        if (!selected || !comment.trim()) return;
        setBusy(true);
        try {
            const res = await api.post(`/tasks/${selected.id}/comments`, {
                message: comment.trim(),
            });
            setTasks((prev) => prev.map((t) => (t.id === selected.id ? res.data : t)));
            setComment("");
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const runInForge = () => {
        if (!selected) return;
        // Persist current task hint to session storage so ForgePage can prefill
        sessionStorage.setItem(
            "forge_prefill",
            JSON.stringify({
                task_id: selected.id,
                brief: selected.brief || selected.description || selected.title,
                brand: selected.brand,
                content_type: selected.content_type,
                aspect_ratio: selected.aspect_ratio,
            }),
        );
        navigate("/forge");
    };

    return (
        <main className="max-w-[1440px] mx-auto px-6 lg:px-10 py-8 lg:py-10 space-y-6">
            <div className="flex items-baseline justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[color:var(--profecia-gold)]">
                        {isAdmin ? "Admin · Task Board" : "My Tasks"}
                    </div>
                    <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--text-main)] leading-tight mt-1">
                        {isAdmin ? "Assigned Work · Team" : `Hello, ${user?.name?.split(" ")[0] || "there"}`}
                    </h2>
                    <p className="text-sm text-[color:var(--text-muted)] font-light mt-2 max-w-2xl">
                        {isAdmin
                            ? "Assign creative briefs to your content creators, track status in real time, and see the pipeline runs they produce for each task."
                            : "Tasks assigned to you appear here. Update status, share progress with your assigner, and jump straight into the Forge to produce the prompt."}
                    </p>
                </div>
                {isAdmin && (
                    <button
                        data-testid={TASKS.newTaskButton}
                        onClick={() => setShowCreate(true)}
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-[color:var(--profecia-gold)] text-white text-sm font-medium hover:bg-[color:var(--profecia-gold-hover)] transition-colors"
                    >
                        <Plus size={14} /> Assign New Task
                    </button>
                )}
            </div>

            {err && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-2">
                    {String(err)}
                </div>
            )}

            {loading ? (
                <div className="py-16 text-center text-sm text-[color:var(--text-muted)]">Loading tasks…</div>
            ) : tasks.length === 0 ? (
                <div className="bg-white rounded-lg border border-[color:var(--border-subtle)] card-ambient p-12 text-center">
                    <ClipboardList size={28} className="mx-auto text-[color:var(--profecia-gold)] opacity-70" />
                    <div className="font-serif text-xl mt-3 text-[color:var(--text-main)]">
                        {isAdmin ? "No tasks yet — assign one to get started" : "No tasks assigned yet"}
                    </div>
                    <p className="text-sm text-[color:var(--text-muted)] font-light mt-2">
                        {isAdmin
                            ? "Click 'Assign New Task' to hand off a brief to a content creator."
                            : "When an admin assigns you a brief, it will show up here."}
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    {STATUSES.map((col) => {
                        const Icon = col.icon;
                        return (
                            <section
                                key={col.key}
                                data-testid={TASKS.column(col.key)}
                                className="bg-[color:var(--surface-hover)] rounded-lg border border-[color:var(--border-subtle)] p-4 min-h-[200px]"
                            >
                                <header className="flex items-center gap-2 mb-3">
                                    <Icon size={14} className="text-[color:var(--profecia-gold)]" />
                                    <h3 className="text-sm font-medium text-[color:var(--text-main)]">
                                        {col.label}
                                    </h3>
                                    <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)] px-1.5 py-0.5 rounded bg-white">
                                        {groups[col.key].length}
                                    </span>
                                </header>
                                <ul className="space-y-2">
                                    {groups[col.key].map((t) => (
                                        <li key={t.id}>
                                            <button
                                                data-testid={TASKS.card(t.id)}
                                                onClick={() => openDetails(t.id)}
                                                className="w-full text-left px-3 py-3 rounded-md bg-white border border-[color:var(--border-subtle)] hover:border-[color:var(--profecia-gold-subtle)] hover:-translate-y-[1px] transition-all duration-200 card-ambient"
                                            >
                                                <div className="text-[color:var(--text-main)] text-sm font-medium leading-snug line-clamp-2">
                                                    {t.title}
                                                </div>
                                                <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--profecia-gold)] mt-1.5">
                                                    {t.brand || "no brand"}
                                                </div>
                                                <div className="flex items-center justify-between mt-2">
                                                    <span className="text-[10px] text-[color:var(--text-muted)]">
                                                        {isAdmin ? `→ ${t.assignee_name || t.assignee_email}` : `by ${t.assigner_name || t.assigner_email}`}
                                                    </span>
                                                    <span className="text-[10px] font-mono text-[color:var(--text-muted)]">{fmtWhen(t.updated_at || t.created_at)}</span>
                                                </div>
                                                {(t.run_ids?.length || 0) > 0 && (
                                                    <div className="text-[10px] text-[color:var(--profecia-gold)] font-mono mt-1.5">
                                                        {t.run_ids.length} linked run{t.run_ids.length > 1 ? "s" : ""}
                                                    </div>
                                                )}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        );
                    })}
                </div>
            )}

            {selected && (
                <TaskDetailDrawer
                    task={selected}
                    onClose={() => setSelectedId(null)}
                    statusDraft={statusDraft}
                    setStatusDraft={setStatusDraft}
                    statusNote={statusNote}
                    setStatusNote={setStatusNote}
                    onSaveStatus={saveStatus}
                    comment={comment}
                    setComment={setComment}
                    onPostComment={postComment}
                    onRunInForge={runInForge}
                    busy={busy}
                />
            )}

            {showCreate && isAdmin && (
                <CreateTaskModal
                    assignables={assignables}
                    forgeConfig={forgeConfig}
                    onClose={() => setShowCreate(false)}
                    onCreated={(t) => {
                        setTasks((prev) => [t, ...prev]);
                        setShowCreate(false);
                    }}
                />
            )}
        </main>
    );
}

function TaskDetailDrawer({
    task, onClose, statusDraft, setStatusDraft, statusNote, setStatusNote,
    onSaveStatus, comment, setComment, onPostComment, onRunInForge, busy,
}) {
    return (
        <div className="fixed inset-0 z-50 flex" data-testid={TASKS.detailPanel}>
            <div className="flex-1 bg-black/30 backdrop-blur-sm" onClick={onClose} />
            <aside className="w-full max-w-[560px] bg-white h-full overflow-y-auto shadow-2xl border-l border-[color:var(--border-subtle)]">
                <div className="sticky top-0 bg-white/95 backdrop-blur-xl border-b border-[color:var(--border-subtle)] px-6 py-4 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[color:var(--profecia-gold)]">
                            {task.brand || "Task"}
                        </div>
                        <h3 data-testid={TASKS.detailTitle} className="font-serif text-2xl text-[color:var(--text-main)] leading-tight mt-1 truncate">
                            {task.title}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-md text-[color:var(--text-muted)] hover:text-[color:var(--text-main)] hover:bg-[color:var(--surface-hover)]"
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {task.description && (
                        <section>
                            <div className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)] mb-1.5">Description</div>
                            <p className="text-sm text-[color:var(--text-main)] font-light leading-relaxed whitespace-pre-wrap">
                                {task.description}
                            </p>
                        </section>
                    )}

                    {task.brief && (
                        <section>
                            <div className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)] mb-1.5">Suggested brief</div>
                            <p className="text-sm text-[color:var(--text-main)] font-light leading-relaxed whitespace-pre-wrap bg-[color:var(--surface-hover)] rounded-md p-3 border border-[color:var(--border-subtle)]">
                                {task.brief}
                            </p>
                        </section>
                    )}

                    <section className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)]">Assignee</div>
                            <div className="text-[color:var(--text-main)] mt-1">{task.assignee_name || task.assignee_email}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)]">Assigner</div>
                            <div className="text-[color:var(--text-main)] mt-1">{task.assigner_name || task.assigner_email}</div>
                        </div>
                        {task.content_type && (
                            <div>
                                <div className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)]">Content type</div>
                                <div className="text-[color:var(--text-main)] mt-1">{task.content_type}</div>
                            </div>
                        )}
                        {task.aspect_ratio && (
                            <div>
                                <div className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)]">Aspect</div>
                                <div className="text-[color:var(--text-main)] mt-1">{task.aspect_ratio}</div>
                            </div>
                        )}
                    </section>

                    <section className="bg-[color:var(--profecia-gold-light)] border border-[color:var(--profecia-gold-subtle)] rounded-md p-4">
                        <div className="flex items-center gap-2">
                            <Send size={14} className="text-[color:var(--profecia-gold)]" />
                            <div className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--profecia-gold)]">
                                Run the Pipeline
                            </div>
                        </div>
                        <p className="text-xs text-[color:var(--text-main)] mt-2 font-light">
                            Jump into the Forge with this task&rsquo;s brief pre-filled. When you finish, the run auto-links back to this task and your assigner sees the status update.
                        </p>
                        <button
                            data-testid={TASKS.runInForge}
                            onClick={onRunInForge}
                            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[color:var(--profecia-gold)] text-white text-sm font-medium hover:bg-[color:var(--profecia-gold-hover)] transition-colors"
                        >
                            Open in Forge <ArrowRight size={14} />
                        </button>
                    </section>

                    <section>
                        <div className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)] mb-2">Status</div>
                        <div className="flex flex-wrap items-center gap-2">
                            <select
                                data-testid={TASKS.statusSelect}
                                value={statusDraft}
                                onChange={(e) => setStatusDraft(e.target.value)}
                                className="px-3 py-2 rounded-md border border-[color:var(--border-subtle)] bg-white text-sm focus:outline-none focus:border-[color:var(--profecia-gold)]"
                            >
                                {STATUSES.map((s) => (
                                    <option key={s.key} value={s.key}>{s.label}</option>
                                ))}
                            </select>
                            <input
                                data-testid={TASKS.statusNote}
                                value={statusNote}
                                onChange={(e) => setStatusNote(e.target.value)}
                                placeholder="Optional note (shared with assigner)"
                                className="flex-1 min-w-[200px] px-3 py-2 rounded-md border border-[color:var(--border-subtle)] focus:outline-none focus:border-[color:var(--profecia-gold)] text-sm"
                            />
                            <button
                                data-testid={TASKS.saveStatus}
                                onClick={onSaveStatus}
                                disabled={busy || statusDraft === task.status && !statusNote.trim()}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[color:var(--profecia-gold)] text-white text-sm hover:bg-[color:var(--profecia-gold-hover)] disabled:opacity-40 transition-colors"
                            >
                                {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Update
                            </button>
                        </div>
                    </section>

                    <section>
                        <div className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)] mb-2 flex items-center gap-2">
                            <MessageSquare size={12} /> Activity
                        </div>
                        <ul className="space-y-3">
                            {(task.updates || []).slice().reverse().map((u, i) => (
                                <li key={i} data-testid={TASKS.updateItem(i)} className="text-xs">
                                    <div className="flex items-baseline justify-between gap-2">
                                        <span className="font-medium text-[color:var(--text-main)]">{u.author_name || "system"}</span>
                                        <span className="font-mono text-[10px] text-[color:var(--text-muted)]">{fmtWhen(u.ts)}</span>
                                    </div>
                                    <div className={`mt-0.5 leading-relaxed ${u.kind === "status" ? "text-[color:var(--profecia-gold)]" : "text-[color:var(--text-main)]"}`}>
                                        {u.message}
                                        {u.kind === "run_link" && u.run_id && (
                                            <a
                                                href={`/forge?run=${u.run_id}`}
                                                className="ml-2 text-[color:var(--profecia-gold)] hover:underline inline-flex items-center gap-1"
                                            >
                                                <ExternalLink size={10} /> open run
                                            </a>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-4 flex items-end gap-2">
                            <textarea
                                data-testid={TASKS.commentInput}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                rows={2}
                                placeholder="Share an update with your assigner…"
                                className="flex-1 resize-none px-3 py-2 rounded-md border border-[color:var(--border-subtle)] focus:outline-none focus:border-[color:var(--profecia-gold)] text-sm"
                            />
                            <button
                                data-testid={TASKS.commentSubmit}
                                onClick={onPostComment}
                                disabled={busy || !comment.trim()}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[color:var(--profecia-gold)] text-white text-sm hover:bg-[color:var(--profecia-gold-hover)] disabled:opacity-40 transition-colors"
                            >
                                <Send size={13} />
                            </button>
                        </div>
                    </section>
                </div>
            </aside>
        </div>
    );
}

function CreateTaskModal({ assignables, forgeConfig, onClose, onCreated }) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [brief, setBrief] = useState("");
    const [assigneeId, setAssigneeId] = useState(assignables[0]?.id || "");
    const [brand, setBrand] = useState("");
    const [contentType, setContentType] = useState("");
    const [aspect, setAspect] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        if (!assigneeId && assignables[0]) setAssigneeId(assignables[0].id);
    }, [assignables, assigneeId]);

    const submit = async () => {
        setErr(null);
        setBusy(true);
        try {
            const { data } = await api.post("/tasks", {
                title,
                description,
                brief,
                assignee_id: assigneeId,
                brand: brand || null,
                content_type: contentType || null,
                aspect_ratio: aspect || null,
            });
            onCreated(data);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={TASKS.createModal}>
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white rounded-lg border border-[color:var(--border-subtle)] w-full max-w-2xl max-h-[90vh] overflow-y-auto card-ambient">
                <div className="sticky top-0 bg-white/95 backdrop-blur-xl border-b border-[color:var(--border-subtle)] px-6 py-4 flex items-center">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[color:var(--profecia-gold)]">New task</div>
                        <h3 className="font-serif text-2xl leading-none mt-1">Assign a brief</h3>
                    </div>
                    <button onClick={onClose} className="ml-auto p-2 rounded-md hover:bg-[color:var(--surface-hover)]" aria-label="Close">
                        <X size={16} />
                    </button>
                </div>
                <div className="p-6 space-y-4">
                    {err && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-2">{String(err)}</div>
                    )}
                    <Field label="Title">
                        <input
                            data-testid={TASKS.titleInput}
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g. Q1 Instagram campaign — Dubai maison"
                            className="w-full px-3 py-2.5 rounded-md border border-[color:var(--border-subtle)] focus:outline-none focus:border-[color:var(--profecia-gold)]"
                        />
                    </Field>
                    <Field label="Description (context for the creator)">
                        <textarea
                            data-testid={TASKS.descriptionInput}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            className="w-full resize-none px-3 py-2.5 rounded-md border border-[color:var(--border-subtle)] focus:outline-none focus:border-[color:var(--profecia-gold)]"
                        />
                    </Field>
                    <Field label="Suggested brief (pre-fills the Forge)">
                        <textarea
                            data-testid={TASKS.briefInput}
                            value={brief}
                            onChange={(e) => setBrief(e.target.value)}
                            rows={4}
                            placeholder="Launch campaign for a Dubai luxury fashion maison…"
                            className="w-full resize-none px-3 py-2.5 rounded-md border border-[color:var(--border-subtle)] focus:outline-none focus:border-[color:var(--profecia-gold)]"
                        />
                    </Field>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Assign to">
                            <select
                                data-testid={TASKS.assigneeSelect}
                                value={assigneeId}
                                onChange={(e) => setAssigneeId(e.target.value)}
                                className="w-full px-3 py-2.5 rounded-md border border-[color:var(--border-subtle)] focus:outline-none focus:border-[color:var(--profecia-gold)] bg-white"
                            >
                                {assignables.map((u) => (
                                    <option key={u.id} value={u.id}>{u.name || u.email}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Brand profile (optional)">
                            <select
                                data-testid={TASKS.brandSelect}
                                value={brand}
                                onChange={(e) => setBrand(e.target.value)}
                                className="w-full px-3 py-2.5 rounded-md border border-[color:var(--border-subtle)] focus:outline-none focus:border-[color:var(--profecia-gold)] bg-white"
                            >
                                <option value="">— any —</option>
                                {(forgeConfig?.brands || []).map((b) => (
                                    <option key={b.name} value={b.name}>{b.name}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Content type (optional)">
                            <select
                                data-testid={TASKS.contentTypeSelect}
                                value={contentType}
                                onChange={(e) => setContentType(e.target.value)}
                                className="w-full px-3 py-2.5 rounded-md border border-[color:var(--border-subtle)] focus:outline-none focus:border-[color:var(--profecia-gold)] bg-white"
                            >
                                <option value="">— any —</option>
                                {(forgeConfig?.content_types || []).map((c) => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Aspect ratio (optional)">
                            <select
                                data-testid={TASKS.aspectSelect}
                                value={aspect}
                                onChange={(e) => setAspect(e.target.value)}
                                className="w-full px-3 py-2.5 rounded-md border border-[color:var(--border-subtle)] focus:outline-none focus:border-[color:var(--profecia-gold)] bg-white"
                            >
                                <option value="">— any —</option>
                                {(forgeConfig?.aspect_ratios || []).map((a) => (
                                    <option key={a} value={a}>{a}</option>
                                ))}
                            </select>
                        </Field>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            data-testid={TASKS.createCancel}
                            onClick={onClose}
                            className="px-4 py-2.5 rounded-md text-[color:var(--text-muted)] hover:text-[color:var(--text-main)] hover:bg-[color:var(--surface-hover)] transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            data-testid={TASKS.createSubmit}
                            onClick={submit}
                            disabled={busy || !title.trim() || !assigneeId}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[color:var(--profecia-gold)] text-white text-sm hover:bg-[color:var(--profecia-gold-hover)] disabled:opacity-40 transition-colors"
                        >
                            {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Create task
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)] mb-1.5 block">
                {label}
            </span>
            {children}
        </label>
    );
}
